/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from "react";
import { useInput, useRecordContext, InputProps, TextInput } from "react-admin";
import { Box, TextField, Typography, Button } from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { ActionCuepoint, ActionCuepointType, StudioClass } from "types/graphql";
import { VideoPlayer } from "lib/components/VideoPlayer";
import { makeStyles } from "@material-ui/core/styles";
import { MUTED_CUEPOINT_INFO_URL } from "lib/constants/classes";
import { colors } from "lib/constants/colors";
import {
  convertTimeStringToSeconds,
  hasOverlappingTimeRanges,
} from "./helpers";

interface CuepointError {
  externalUrl?: string;
  infoUrl?: string;
  time?: string;
  overlap?: boolean;
}

interface TouchedFields {
  externalUrl?: boolean;
  infoUrl?: boolean;
  startsAtTime?: boolean;
  endsAtTime?: boolean;
}

const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (e) {
    return false;
  }
};

const isValidTimeRange = (startTime: string, endTime: string): boolean => {
  const startSeconds = convertTimeStringToSeconds(startTime);
  const endSeconds = convertTimeStringToSeconds(endTime);
  return endSeconds > startSeconds;
};

const validateActionCuepoints = (
  cuepoints: ActionCuepoint[] | undefined
): string | undefined => {
  if (!cuepoints?.length) {
    return undefined;
  }

  const errors: string[] = [];

  // Check for URL validity
  cuepoints.forEach((cuepoint, index) => {
    if (!cuepoint.externalUrl || !isValidUrl(cuepoint.externalUrl)) {
      errors.push(
        `External URL is required and must be valid for cuepoint ${index + 1}`
      );
    }
    if (cuepoint.infoUrl && !isValidUrl(cuepoint.infoUrl)) {
      errors.push(`Info URL must be valid for cuepoint ${index + 1}`);
    }
  });

  // Check for overlapping time ranges
  const overlap = hasOverlappingTimeRanges(cuepoints);
  if (overlap) {
    errors.push(
      `Time ranges overlap between cuepoint ${
        overlap.index1 + 1
      } and cuepoint ${overlap.index2 + 1}`
    );
  }

  return errors.length > 0 ? errors.join(". ") : undefined;
};

const useStyles = makeStyles(() => ({
  classSectionsInput: {
    display: "flex",
  },
  classSectionsInputLeft: {
    flex: "1",
  },
  classSectionsInputRight: {
    flex: "1",
    paddingLeft: "8px",
  },
  cuepoints: {
    marginLeft: "30px",
  },
  cuepoint: {
    display: "flex",
    marginTop: "6px",
    flexDirection: "column",
  },
  cuepointRow: {
    display: "flex",
    alignItems: "center",
  },
  cuepointSecondRow: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "30px",
    marginTop: "6px",
  },
  cuepointInputs: {
    display: "flex",
    alignItems: "center",
  },
  cuepointTime: {
    maxWidth: "100px",
  },
  cuepointDash: {
    fontSize: "20px",
    fontWeight: "bold",
    margin: "0 8px",
  },
  removeButton: {
    cursor: "pointer",
    marginLeft: "8px",
    color: "rgba(0, 0, 0, 0.5)",
  },
  addButton: {
    cursor: "pointer",
    marginTop: "8px",
    marginBottom: "30px",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    color: "rgba(0, 0, 0, 0.5)",
  },
  addButtonText: {
    marginLeft: "4px",
  },
  errorText: {
    color: colors.redMagenta[7],
    marginTop: "4px",
    fontSize: "0.75rem",
  },
  customButton: {
    marginTop: "9px",
    marginLeft: "8px",
  },
  cuepointTimeError: {
    color: colors.redMagenta[7],
    fontSize: "0.75rem",
    marginTop: "4px",
    marginLeft: "0",
  },
}));

const useVideoStyles = makeStyles(() => ({
  progress: {
    display: "flex",
    justifyContent: "center",
  },
  video: {
    width: "100%",
  },
}));

export const ActionCuepointsTab: React.FC<InputProps> = (props) => {
  const record = useRecordContext<StudioClass>(props);
  const videoUrl = record?.video?.hlsPlaybackUrls?.defaultUrl;
  const classes = useStyles();
  const videoClasses = useVideoStyles();
  const {
    input: { value, onChange },
  } = useInput({
    ...props,
    validate: validateActionCuepoints,
  });
  const [cuepointErrors, setCuepointErrors] = useState<
    Record<number, CuepointError>
  >({});
  const [touchedFields, setTouchedFields] = useState<
    Record<number, TouchedFields>
  >({});
  const [externalPreviewUrl, setExternalPreviewUrl] = useState(
    record.externalPreviewUrl
  );

  // Validate all cuepoints whenever value changes or on mount
  useEffect(() => {
    if (!value?.length) {
      return;
    }

    const errors: Record<number, CuepointError> = {};

    // URL validation
    value.forEach((cuepoint: ActionCuepoint, index: number) => {
      // Only validate if fields have been touched
      if (touchedFields[index]?.externalUrl) {
        if (!cuepoint.externalUrl) {
          errors[index] = {
            ...errors[index],
            externalUrl: "External URL is required",
          };
        } else if (!isValidUrl(cuepoint.externalUrl)) {
          errors[index] = {
            ...errors[index],
            externalUrl: "Please enter a valid URL (e.g., https://example.com)",
          };
        }
      }

      if (
        touchedFields[index]?.infoUrl &&
        cuepoint.infoUrl &&
        !isValidUrl(cuepoint.infoUrl)
      ) {
        errors[index] = {
          ...errors[index],
          infoUrl: "Please enter a valid URL (e.g., https://example.com)",
        };
      }

      // Validate time range if either time field is touched
      if (
        touchedFields[index]?.startsAtTime ||
        touchedFields[index]?.endsAtTime
      ) {
        if (!isValidTimeRange(cuepoint.startsAtTime, cuepoint.endsAtTime)) {
          errors[index] = {
            ...errors[index],
            time: "End time must be greater than start time",
          };
        }
      }
    });

    // Check for overlapping ranges
    const overlap = hasOverlappingTimeRanges(value);
    if (overlap) {
      // Mark both overlapping cuepoints with error
      errors[overlap.index1] = {
        ...errors[overlap.index1],
        time: `Overlaps with cuepoint ${overlap.index2 + 1}`,
        overlap: true,
      };
      errors[overlap.index2] = {
        ...errors[overlap.index2],
        time: `Overlaps with cuepoint ${overlap.index1 + 1}`,
        overlap: true,
      };
    }

    setCuepointErrors(errors);
  }, [value, touchedFields]);

  const validateCuepoint = (
    cuepoint: ActionCuepoint,
    index: number,
    existingErrors: Record<number, CuepointError> = {}
  ): void => {
    const errors = { ...existingErrors };

    // Only validate if fields have been touched
    if (touchedFields[index]?.externalUrl) {
      if (!cuepoint.externalUrl) {
        errors[index] = {
          ...errors[index],
          externalUrl: "External URL is required",
        };
      } else if (!isValidUrl(cuepoint.externalUrl)) {
        errors[index] = {
          ...errors[index],
          externalUrl: "Please enter a valid URL (e.g., https://example.com)",
        };
      } else {
        delete errors[index]?.externalUrl;
      }
    }

    if (touchedFields[index]?.infoUrl) {
      if (cuepoint.infoUrl && !isValidUrl(cuepoint.infoUrl)) {
        errors[index] = {
          ...errors[index],
          infoUrl: "Please enter a valid URL (e.g., https://example.com)",
        };
      } else {
        delete errors[index]?.infoUrl;
      }
    }

    // Validate time range if either time field is touched
    if (
      touchedFields[index]?.startsAtTime ||
      touchedFields[index]?.endsAtTime
    ) {
      if (!isValidTimeRange(cuepoint.startsAtTime, cuepoint.endsAtTime)) {
        errors[index] = {
          ...errors[index],
          time: "End time must be greater than start time",
        };
      } else {
        delete errors[index]?.time;
      }
    }

    // Remove the index entirely if no errors
    if (Object.keys(errors[index] || {}).length === 0) {
      delete errors[index];
    }

    setCuepointErrors(errors);
  };

  const updateEndTime = (cuepointIndex: number, newEndTime: string): void => {
    const newCuepoints = [...value];
    const newTimeTimeSec = convertTimeStringToSeconds(newEndTime);

    newCuepoints[cuepointIndex].endsAtTime = newEndTime;
    newCuepoints[cuepointIndex].endsAtTimeSec = newTimeTimeSec;

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [cuepointIndex]: {
        ...prev[cuepointIndex],
        endsAtTime: true,
      },
    }));

    onChange(newCuepoints);
    validateCuepoint(newCuepoints[cuepointIndex], cuepointIndex);
  };

  const updateStartTime = (
    cuepointIndex: number,
    newStartTime: string
  ): void => {
    const newCuepoints = [...value];
    const newTimeTimeSec = convertTimeStringToSeconds(newStartTime);

    newCuepoints[cuepointIndex].startsAtTime = newStartTime;
    newCuepoints[cuepointIndex].startsAtTimeSec = newTimeTimeSec;

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [cuepointIndex]: {
        ...prev[cuepointIndex],
        startsAtTime: true,
      },
    }));

    onChange(newCuepoints);
    validateCuepoint(newCuepoints[cuepointIndex], cuepointIndex);
  };

  const handleFieldChange = (
    cuepointIndex: number,
    field: keyof ActionCuepoint,
    newValue: string
  ): void => {
    const newCuepoints = [...value];
    newCuepoints[cuepointIndex][field] = newValue;

    if (field === "type" && newValue === ActionCuepointType.ExternalMusicLink) {
      newCuepoints[cuepointIndex].actionCopy = "Practice with Music";
      newCuepoints[cuepointIndex].externalUrl =
        record?.externalPreviewUrl || "";
    }

    // Mark field as touched for URL fields
    if (field === "externalUrl" || field === "infoUrl") {
      setTouchedFields((prev) => ({
        ...prev,
        [cuepointIndex]: {
          ...prev[cuepointIndex],
          [field]: true,
        },
      }));
    }

    onChange(newCuepoints);
    validateCuepoint(newCuepoints[cuepointIndex], cuepointIndex);
  };

  const handleAddCuepoint = (): void => {
    const newCuepoints = [...value];
    const newCuepoint = {
      startsAtTime: "00:00",
      endsAtTime: "00:10",
      startsAtTimeSec: 0,
      endsAtTimeSec: 10,
      type: ActionCuepointType.ExternalMusicLink,
      actionCopy: "Practice with Music",
      externalUrl: record?.externalPreviewUrl || "",
      infoUrl: MUTED_CUEPOINT_INFO_URL,
    };
    newCuepoints.push(newCuepoint);
    onChange(newCuepoints);
  };

  const handleDeleteCuepoint = (index: number): void => {
    const newCuepoints = [...value];
    newCuepoints.splice(index, 1);
    onChange(newCuepoints);

    // Remove touched state and errors for the deleted cuepoint
    const newTouchedFields = { ...touchedFields };
    const newErrors = { ...cuepointErrors };
    delete newTouchedFields[index];
    delete newErrors[index];

    // Shift indices down
    Object.keys(newTouchedFields).forEach((fieldIndex) => {
      const i = parseInt(fieldIndex);
      if (i > index) {
        newTouchedFields[i - 1] = newTouchedFields[i];
        delete newTouchedFields[i];
      }
    });
    Object.keys(newErrors).forEach((errorIndex) => {
      const i = parseInt(errorIndex);
      if (i > index) {
        newErrors[i - 1] = newErrors[i];
        delete newErrors[i];
      }
    });

    setTouchedFields(newTouchedFields);
    setCuepointErrors(newErrors);
  };

  const hasErrors = Object.keys(cuepointErrors).length > 0;

  const setAllExternalMusicLinkUrls = () => {
    const newCuepoints = value.map((cuepoint: ActionCuepoint) => ({
      ...cuepoint,
      externalUrl:
        cuepoint.type === ActionCuepointType.ExternalMusicLink
          ? externalPreviewUrl
          : cuepoint.externalUrl,
    }));
    onChange(newCuepoints);
  };

  return (
    <Box className={classes.classSectionsInput}>
      <Box className={classes.classSectionsInputLeft}>
        {hasErrors && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            Please fix the validation errors before saving
          </Typography>
        )}
        <TextInput
          placeholder="External Preview URL"
          source="externalPreviewUrl"
          variant="outlined"
          size="small"
          onChange={(e) => setExternalPreviewUrl(e.target.value)}
        />
        <Button
          className={classes.customButton}
          color="primary"
          variant="contained"
          onClick={setAllExternalMusicLinkUrls}
        >
          Apply URL to all external music link cuepoints
        </Button>
        <Box className={classes.cuepoints}>
          {value &&
            value.map((cuepoint: ActionCuepoint, index: number) => (
              <Box key={index} className={classes.cuepoint}>
                <Box className={classes.cuepointRow}>
                  <TextField
                    placeholder="hh:mm:ss"
                    value={cuepoint.startsAtTime}
                    onChange={(e) => updateStartTime(index, e.target.value)}
                    onBlur={(e) => updateStartTime(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.cuepointTime}
                    error={!!cuepointErrors[index]?.time}
                  />
                  <Box className={classes.cuepointDash}>-</Box>
                  <TextField
                    placeholder="hh:mm:ss"
                    value={cuepoint.endsAtTime}
                    onChange={(e) => updateEndTime(index, e.target.value)}
                    onBlur={(e) => updateEndTime(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.cuepointTime}
                    error={!!cuepointErrors[index]?.time}
                  />
                  <Box className={classes.cuepointDash}> </Box>
                  <TextField
                    select
                    value={cuepoint.type}
                    onChange={(e) =>
                      handleFieldChange(index, "type", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    style={{ width: 200 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    {Object.values(ActionCuepointType).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </TextField>
                  <Box
                    className={classes.removeButton}
                    onClick={() => handleDeleteCuepoint(index)}
                  >
                    <HighlightOffIcon />
                  </Box>
                </Box>
                {cuepointErrors[index]?.time && (
                  <Typography className={classes.cuepointTimeError}>
                    {cuepointErrors[index]?.time}
                  </Typography>
                )}
                <Box className={classes.cuepointSecondRow}>
                  <Box className={classes.cuepointInputs}>
                    <TextField
                      value={cuepoint.actionCopy}
                      onChange={(e) =>
                        handleFieldChange(index, "actionCopy", e.target.value)
                      }
                      placeholder="Action Copy"
                      variant="outlined"
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <Box className={classes.cuepointDash}> </Box>
                    <TextField
                      value={cuepoint.externalUrl}
                      onChange={(e) =>
                        handleFieldChange(index, "externalUrl", e.target.value)
                      }
                      placeholder="External URL"
                      variant="outlined"
                      size="small"
                      error={!!cuepointErrors[index]?.externalUrl}
                      required
                      style={{ flex: 1 }}
                    />
                    <Box className={classes.cuepointDash}> </Box>
                    <TextField
                      value={cuepoint.infoUrl || ""}
                      onChange={(e) =>
                        handleFieldChange(index, "infoUrl", e.target.value)
                      }
                      placeholder="Info URL"
                      variant="outlined"
                      size="small"
                      error={!!cuepointErrors[index]?.infoUrl}
                      style={{ flex: 1 }}
                    />
                  </Box>
                  {(cuepointErrors[index]?.externalUrl ||
                    cuepointErrors[index]?.infoUrl) && (
                    <Typography className={classes.errorText}>
                      {cuepointErrors[index]?.externalUrl}{" "}
                      {cuepointErrors[index]?.externalUrl &&
                        cuepointErrors[index]?.infoUrl &&
                        " • "}{" "}
                      {cuepointErrors[index]?.infoUrl}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          <Typography className={classes.addButton} onClick={handleAddCuepoint}>
            <AddCircleOutlineIcon />
            <span className={classes.addButtonText}>Add Action Cuepoint</span>
          </Typography>
        </Box>
      </Box>
      <Box className={classes.classSectionsInputRight}>
        {!videoUrl && <Typography>No video preview available yet.</Typography>}
        {videoUrl && (
          <VideoPlayer playbackUrl={videoUrl} classes={videoClasses} />
        )}
      </Box>
    </Box>
  );
};
